import { swap } from "../core/swap";
import { getConfig, setConfig } from "../constants/config";
import { getRouteDetails } from "../helpers/getRouteDetails";
import { TokenListByAddress } from "../../common/constants/tokens";
import { getSwapTypes } from "../helpers/getSwapTypes";
import { getTokenIn } from "../helpers/getTokenIn";
import { getTokenOut } from "../helpers/getTokenOut";
import { getTotalAmountIn } from "../helpers/getTotalAmountIn";
import { getTotalAmountOut } from "../helpers/getTotalAmountOut";
import { getRoute } from "../core/getRoute";
import { checkApproval } from "../core/checkApproval";
import { giveApproval } from "../core/giveApproval";
import { generateCalldata } from "../helpers/generateCalldata";

/**
 * Represents a swap route with execution capabilities.
 * Contains route details and methods for executing swaps, checking/giving approvals.
 */
export class Route {
  /**
   * Creates a new Route instance.
   *
   * @param {Object} routePayload - Route information from getRoute
   * @param {Function} getSigner - Function that returns the current signer
   * @param {Object} options - Route options
   */
  constructor(routePayload, getSigner, options) {
    this.amountIn = routePayload.amountIn;
    this.amountInFormatted = routePayload.amountInFormatted;
    this.includesNative = routePayload.includesNative;
    this.pathCount = routePayload.pathCount;
    this.pathPercentages = routePayload.pathPercentages;
    this.amountOut = routePayload.amountOut;
    this.amountOutFormatted = routePayload.amountOutFormatted;
    this.route = routePayload.route;
    this.rebate = routePayload.rebate;
    this.tokenIn = routePayload.tokenIn;
    this.tokenOut = routePayload.tokenOut;
    this.tokenInUsd = routePayload.tokenInUsd;
    this.tokenOutUsd = routePayload.tokenOutUsd;
    this.swapFee = routePayload.swapFee;
    this.priceImpactPercentage = routePayload.priceImpactPercentage;
    this.getSigner = getSigner;
    this.options = options;
  }

  /**
   * Executes the swap using the route.
   *
   * @param {Object} params - Swap parameters
   * @param {Array} [params.route=this.route] - Route to use for swap
   * @param {boolean} [params.includesNative=this.includesNative] - Whether route includes native token
   * @param {ethers.Signer} [params.signer=this.getSigner()] - Signer for transaction
   * @param {Object} [params.options] - Swap options
   * @param {boolean} [params.options.skipApproval=true] - Skip token approval check
   * @param {boolean} [params.options.skipCheckApproval=false] - Skip approval transaction
   * @param {Object} [params.slippage] - Slippage configuration
   * @param {string} [params.slippage.slippageAmount=config.slippage] - Slippage tolerance
   * @param {boolean} [params.slippage.isRaw=true] - If true, slippage is in raw units
   * @param {boolean} [params.slippage.isBps=false] - If true, slippage is in basis points
   * @param {number} [params.slippage.outTokenDecimals] - Output token decimals
   *
   * @returns {Promise<ethers.ContractTransaction>} The swap transaction
   *
   * @example
   * // Execute swap with default options
   * const tx = await route.swap();
   *
   * @example
   * // Execute swap with custom slippage
   * const tx = await route.swap({
   *   slippage: {
   *     slippageAmount: "1.0", // 1% slippage
   *     isBps: false
   *   }
   * });
   */
  async swap({
    route = this.route,
    includesNative = this.includesNative,
    signer = this.getSigner(),
    options = {},
    slippage = {},
  } = {}) {
    const defaultOptions = {
      skipApproval: true,
      skipCheckApproval: false,
      isRaw: true,
      outTokenDecimals: undefined,
    };

    const defaultSlippage = {
      slippageAmount: getConfig().slippage,
      isBps: false,
    };
    const mergedOptions = { ...defaultOptions, ...options };
    const mergedSlippage = { ...defaultSlippage, ...slippage };

    return await swap({
      route,
      includesNative,
      signer,
      options: mergedOptions,
      slippage: mergedSlippage,
    });
  }

  /**
   * Encodes the calldata for this route's swap transaction without executing it.
   * This is useful for transaction simulation using eth_call.
   *
   * @param {Object} params - Parameters for encoding swap calldata
   * @param {string|ethers.Signer} [params.from=this.getSigner()] - From address or signer
   * @param {Array} [params.route=this.route] - Route to use for swap
   * @param {boolean} [params.includesNative=this.includesNative] - Whether route includes native token
   * @param {Object} [params.slippage] - Slippage configuration
   * @param {string} [params.slippage.slippageAmount=config.slippage] - Slippage tolerance
   * @param {boolean} [params.slippage.isRaw=true] - If true, slippage is in raw units
   * @param {boolean} [params.slippage.isBps=false] - If true, slippage is in basis points
   * @param {number} [params.slippage.outTokenDecimals] - Output token decimals
   *
   * @returns {Promise<Object>} Object containing the encoded calldata and transaction value
   *
   * @example
   * // Get calldata with default options
   * const tx = await route.generateCalldata();
   *
   * @example
   * // Get calldata with custom from address
   * const tx = await route.generateCalldata({
   *   from: "0xa2a9dd657D44e46E2d1843B8784eFc3dE3Cf3A57"
   * });
   *
   * // Use with eth_call
   * const result = await provider.call(tx);
   */
  async generateCalldata({
    from = this.getSigner(),
    route = this.route,
    includesNative = this.includesNative,
    slippage = {},
    options = {},
  } = {}) {
    const defaultOptions = {
      isRaw: true,
      outTokenDecimals: undefined,
    };
    const defaultSlippage = {
      slippageAmount: getConfig().slippage,
      isBps: false,
    };
    const mergedOptions = { ...defaultOptions, ...options };
    const mergedSlippage = { ...defaultSlippage, ...slippage };

    return await generateCalldata({
      from,
      route,
      includesNative,
      slippage: mergedSlippage,
      options: mergedOptions,
    });
  }

  /**
   * Refreshes the route with current market prices.
   *
   * @returns {Promise<Route>} A new Route instance with updated prices
   *
   * @example
   * const updatedRoute = await route.refresh();
   * console.log(updatedRoute.amountOutFormatted); // Updated output amount
   */
  async refresh() {
    const routePayload = await getRoute(
      this.tokenIn,
      this.tokenOut,
      this.amountInFormatted,
      this.options
    );

    return new Route(routePayload, this.getSigner, this.options);
  }

  /**
   * Checks if token approval is needed for the swap.
   *
   * @param {Object} params - Check parameters
   * @param {Array} [params.variable=this.route] - Route to check
   * @param {ethers.Signer} [params.signer=this.getSigner()] - Signer to use
   * @param {boolean} [params.includesNative=this.includesNative] - Whether route includes native token
   * @param {Object} [params.options] - Check options
   * @param {boolean} [params.options.isRaw=true] - If true, amounts are in raw units
   * @param {number} [params.options.chainId=config.chainId] - Chain ID to use
   *
   * @returns {Promise<boolean>} True if approval is needed
   *
   * @example
   * const needsApproval = await route.checkApproval();
   * if (needsApproval) {
   *   await route.giveApproval();
   * }
   */
  async checkApproval({
    variable = this.route,
    signer = this.getSigner(),
    includesNative = this.includesNative,
    options = {},
  } = {}) {
    const defaultOptions = { isRaw: true, chainId: getConfig().chainId };
    const mergedOptions = { ...defaultOptions, ...options };
    return await checkApproval({
      variable,
      signer,
      includesNative,
      options: mergedOptions,
    });
  }

  /**
   * Gives token approval for the swap.
   *
   * @param {Object} params - Approval parameters
   * @param {Array} [params.variable=this.route] - Route to approve
   * @param {ethers.Signer} [params.signer=this.getSigner()] - Signer to use
   * @param {string} [params.amount=this.amountIn] - Amount to approve
   *   - When provided, overrides the route's default amount
   *   - If isRaw=true: Amount in raw units (wei)
   *   - If isRaw=false: Amount in formatted units (e.g., "1.0")
   * @param {Object} [params.options] - Approval options
   * @param {boolean} [params.options.isRaw=true] - If true, amount is in raw units
   * @param {number} [params.options.chainId=config.chainId] - Chain ID to use
   *
   * @returns {Promise<ethers.ContractTransaction>} The approval transaction
   *
   * @example
   * // Use route's default amount
   * const tx = await route.giveApproval();
   * await tx.wait(); // Wait for approval to be mined
   *
   * @example
   * // Override with custom amount
   * const tx = await route.giveApproval({
   *   amount: "10.0",
   *   options: { isRaw: false }
   * });
   * await tx.wait();
   */
  async giveApproval({
    variable = this.route,
    signer = this.getSigner(),
    amount = this.amountIn,
    options = {},
  } = {}) {
    const defaultOptions = { isRaw: true, chainId: getConfig().chainId };
    const mergedOptions = { ...defaultOptions, ...options };
    return await giveApproval({
      variable,
      signer,
      amount,
      options: mergedOptions,
    });
  }

  async getRouteDetails(route = this.route) {
    return await getRouteDetails(route);
  }

  getSwapTypes(route = this.route) {
    return getSwapTypes(route);
  }

  getTokenIn(route = this.route) {
    return getTokenIn(route);
  }

  getTokenOut(route = this.route) {
    return getTokenOut(route);
  }

  async getTotalAmountIn(route = this.route) {
    return await getTotalAmountIn(route);
  }

  async getTotalAmountOut(route = this.route) {
    return await getTotalAmountOut(route);
  }

  getConfig() {
    return getConfig();
  }

  setConfig(options) {
    setConfig(options);
  }
}
